import { useEffect } from "react";
import { useForm } from "react-hook-form";

import { Form } from "@/components/Form/Form";
import { FormTextArea } from "@/components/Form/FormTextArea";
import type { ModalBaseProps } from "@/components/Modal/Modal";
import { Modal } from "@/components/Modal/Modal";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTranslation } from "@/translations";

const FORM_ID = "ticket-ai-copilot-modal-form";

type TicketCopilotProps = ModalBaseProps & {
  onGenerated: (message: string) => void;
  ticketId: string;
  defaultInput?: FormValues;
};

interface FormValues {
  input: string;
}

export function TicketAICopilotModal({
  isOpened,
  onOpenChange,
  onGenerated,
  ticketId,
  defaultInput,
}: TicketCopilotProps): React.ReactNode {
  const { t } = useTranslation();
  const form = useForm<FormValues>({ defaultValues: defaultInput });

  useEffect(() => {
    if (defaultInput) {
      form.reset(defaultInput);
    }
  }, [defaultInput, form]);

  const ticketSuggestion = ticketMutations.useGetTicketCopilotSuggestion(ticketId);

  async function handleSubmit() {
    const input = form.getValues("input");

    const suggestion = await ticketSuggestion.mutateAsync({ additionalInfo: input }).then((res) => res.response);

    onGenerated(suggestion);

    form.reset();
    onOpenChange(false);
  }

  return (
    <Modal.Root
      title={t("page.tickets.details.copilot.modal.title")}
      description={t("page.tickets.details.copilot.modal.description")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      shouldCloseOnClickOutside={false}
      shouldCloseOnEsc={false}
      isNested
    >
      <Modal.Body>
        <Form.Root id={FORM_ID} formMethods={form} onSubmit={handleSubmit}>
          <FormField label={t("page.tickets.details.copilot.modal.text-area.label")}>
            <FormTextArea
              name="input"
              data-testid="ai-copilot-input"
              placeholder={t("page.tickets.details.copilot.modal.text-area.placeholder")}
            />
          </FormField>
        </Form.Root>
      </Modal.Body>

      <Modal.Actions>
        <Button
          type="button"
          variant="outline"
          tone="primary"
          disabled={ticketSuggestion.isPending}
          onClick={() => onOpenChange(false)}
        >
          {t("common.action.cancel")}
        </Button>
        <Button
          variant="ai"
          form={FORM_ID}
          data-testid="submit-ai-response"
          type="submit"
          isLoading={ticketSuggestion.isPending}
        >
          {t("page.tickets.details.copilot.modal.button")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
